import { useLayoutEffect } from 'react';
import axios from 'axios';

export interface AuthorizationConfig {
    /**
     * URL pattern to match (e.g., '/api/financing')
     */
    urlPattern: string;
    /**
     * The full authorization header value (e.g., "Bearer token", "Basic credentials")
     */
    authorizationValue: string;
}

/**
 * Hook to set up an axios request interceptor that adds Authorization header
 * Supports multiple authorization values for different API endpoints
 *
 * @param authConfigs - Array of configs mapping URL patterns to auth values
 *
 * @example
 * // Single API with auth
 * useAxiosAuthInterceptor([
 *   { urlPattern: '/api/financing', authorizationValue: 'Bearer token-123' }
 * ]);
 *
 * @example
 * // Multiple APIs with different auth tokens
 * useAxiosAuthInterceptor([
 *   { urlPattern: '/api/financing', authorizationValue: 'Bearer financing-token-123' },
 *   { urlPattern: '/api/prequals', authorizationValue: 'Bearer prequals-token-456' }
 * ]);
 *
 * @example
 * // Custom auth scheme (not Bearer)
 * useAxiosAuthInterceptor([
 *   { urlPattern: '/api/custom', authorizationValue: 'Basic dXNlcjpwYXNz' }
 * ]);
 *
 * @example
 * // No auth (or conditionally disabled)
 * useAxiosAuthInterceptor([]);
 */
export const useAxiosAuthInterceptor = (authConfigs?: AuthorizationConfig[]) => {
    useLayoutEffect(() => {
        // Don't set up interceptor if no config or empty array
        if (!authConfigs || authConfigs.length === 0) {
            return;
        }

        // Add request interceptor
        const interceptorId = axios.interceptors.request.use(
            requestConfig => {
                // Skip if Authorization header already present
                if (requestConfig.headers.Authorization) {
                    return requestConfig;
                }

                // Match based on baseURL (API client sets this)
                const baseURL = requestConfig.baseURL ?? '';
                const matchingConfig = authConfigs.find(({ urlPattern }) => baseURL === urlPattern);

                if (matchingConfig) {
                    requestConfig.headers.Authorization = matchingConfig.authorizationValue;
                }

                return requestConfig;
            },
            error => {
                return Promise.reject(error);
            },
        );

        // Cleanup: eject the interceptor when component unmounts or config changes
        return () => {
            axios.interceptors.request.eject(interceptorId);
        };
    }, [authConfigs]);
};
